/*
  COR-1684: the conference app derives its whole Material palette from a single source
  colour, and the organizer may only change that colour's hue. Saturation and brightness
  are pinned so the colour cannot disappear on the app's light and dark surfaces.
  The defaults below reproduce the design's #ff8875 at hue 8.
*/
import { hexToRGB } from '@/utils'

export const BRAND_SATURATION = 0.54
export const BRAND_BRIGHTNESS = 1
export const DEFAULT_HUE = 8

/*
  Per 60 degree sector, which of [chroma, secondary, 0] each of R, G and B takes.
  Sector 0 is (C, X, 0), sector 1 is (X, C, 0), and so on.
*/
const CHANNEL_ORDER = [
  [0, 1, 2],
  [1, 0, 2],
  [2, 0, 1],
  [2, 1, 0],
  [1, 2, 0],
  [0, 2, 1],
]

export const hsvToHex = (h, s, v) => {
  const hue = (((h % 360) + 360) % 360) / 60
  const chroma = v * s
  const secondary = chroma * (1 - Math.abs((hue % 2) - 1))
  const min = v - chroma
  const channels = [chroma, secondary, 0]

  return `#${CHANNEL_ORDER[Math.floor(hue) % 6]
    .map(index => Math.round((channels[index] + min) * 255))
    .map(channel => `0${channel.toString(16)}`.slice(-2))
    .join('')}`
}

export const hexToHue = hex => {
  const rgb = hexToRGB(String(hex || ''))
  if (!rgb) return null

  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(channel => channel / 255)
  const max = Math.max(r, g, b)
  const delta = max - Math.min(r, g, b)
  if (delta === 0) return 0

  const sector =
    max === r ? ((g - b) / delta) % 6 : max === g ? (b - r) / delta + 2 : (r - g) / delta + 4

  return Math.round((((sector * 60) % 360) + 360) % 360)
}

// The brand colour desaturated towards white, keeping the hue.
const PALETTE_SATURATION_RATIOS = [1, 0.4, 0.1, 0]

export const getBrandColor = hue => hsvToHex(hue, BRAND_SATURATION, BRAND_BRIGHTNESS)

export const getBrandPalette = hue =>
  PALETTE_SATURATION_RATIOS.map(ratio => hsvToHex(hue, BRAND_SATURATION * ratio, BRAND_BRIGHTNESS))
